import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { EntityActionMenu, type EntityAction } from './EntityActionMenu.js';

export interface EntityHeaderAction extends EntityAction {
  icon?: ReactNode;
  href?: string;
  testId?: string;
}
export function EntityHeader({
  backTo,
  backLabel,
  title,
  subtitle,
  chips,
  primary,
  secondary = [],
  overflow = [],
  children,
}: {
  backTo: string;
  backLabel: string;
  title: string;
  subtitle?: ReactNode;
  chips?: ReactNode[];
  primary: EntityHeaderAction;
  secondary?: EntityHeaderAction[];
  overflow?: EntityAction[];
  children?: ReactNode;
}): ReactElement {
  const visible = [primary, ...secondary.filter((action) => !action.danger)];
  const menu = [
    ...(primary.danger ? [primary] : []),
    ...secondary.filter((action) => action.danger),
    ...overflow,
  ];
  return (
    <header className="entity-header">
      <div className="entity-header-identity">
        <Link to={backTo} aria-label={backLabel} className="entity-back-link">
          <ArrowLeftOutlined aria-hidden /> {backLabel}
        </Link>
        <div className="entity-header-title">
          <h1>{title}</h1>
          {chips?.slice(0, 3)}
        </div>
        {subtitle ? <div className="entity-meta">{subtitle}</div> : null}
      </div>
      <div className="entity-header-actions">
        {visible
          .filter((action) => !action.danger)
          .map((action, index) => (
            <Button
              key={action.key}
              type={index === 0 ? 'primary' : 'default'}
              icon={action.icon}
              {...(action.href ? { href: action.href } : {})}
              disabled={action.disabled ?? false}
              data-testid={action.testId ?? `entity-action-${action.key}`}
              onClick={() => void action.onClick()}
            >
              {action.label}
            </Button>
          ))}
        {menu.length > 0 ? <EntityActionMenu actions={menu} /> : null}
      </div>
      {children ? <div className="entity-header-extra">{children}</div> : null}
    </header>
  );
}
